import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {MetadataPropertyEntity} from "../../api/entities/replancity_MetadataProperty";
import {DataById, FeatureProperties} from "../map/types";
import {BaseEntityNameLess} from "../../api/entities/BaseEntity";

export type FeatureWithParent = {
    parent?: { [key: string]: any },
    feature: GeoJSON.Feature<GeoJSON.Geometry, Omit<FeatureProperties, 'layerId'>>
};

type State = {
    dataByEntityName: DataById<BaseEntityNameLess>;
    formDataByEntityName: DataById<{ data: BaseEntityNameLess; isChanged: boolean }>;
    metadataByEntityName: DataById<MetadataPropertyEntity[]>;
    featuresWithParentByEntityName: {
        [key: string]: FeatureWithParent;
    };
}

export const initialState: State = {
    dataByEntityName: {},
    formDataByEntityName: {},
    metadataByEntityName: {},
    featuresWithParentByEntityName: {},
}

const entitySlice = createSlice({
    name: 'entity',
    initialState,
    reducers: {
        setEntity(state, action: PayloadAction<{ entityName: string; entity?: any; }>) {
            console.log('==setEntity', action.payload);
            const {entityName, entity} = action.payload;
            state.dataByEntityName[entityName] = entity ?? {};
        },
        setFormEntity(state, action: PayloadAction<{ entityName: string; entity?: any; }>) {
            const {entityName, entity} = action.payload;
            state.formDataByEntityName[entityName] = {
                ...state.formDataByEntityName[entityName],
                data: entity ?? {}
            };
        },
        setEntityMetadata(state, action: PayloadAction<{ entityName: string; metadata: MetadataPropertyEntity[]; }>) {
            const {entityName, metadata} = action.payload;
            state.metadataByEntityName[entityName] = metadata ?? {};
        },
        setFeatureWithParent(state, action: PayloadAction<{
            entityName: string;
            parent?: { [key: string]: any };
            feature?: GeoJSON.Feature<GeoJSON.Geometry, Omit<FeatureProperties, 'layerId'>>;
        }>) {
            const {entityName, parent, feature} = action.payload;
            state.featuresWithParentByEntityName[entityName] = {
                ...state.featuresWithParentByEntityName[entityName],
                ...(parent ? {parent} : {}),
                ...(feature ? {feature} : {}),
            };
        },
        removeEntity(state, action: PayloadAction<string>) {
            const {[action.payload]: deletedEntity, ...restEntities} = state.dataByEntityName;
            const {[action.payload]: deletedFormEntity, ...restFormEntities} = state.formDataByEntityName;
            const {[action.payload]: deletedMetaData, ...restMetaData} = state.metadataByEntityName;
            return {
                ...state,
                dataByEntityName: restEntities,
                formDataByEntityName: restFormEntities,
                metadataByEntityName: restMetaData,
            }
        },
        removeFeature(state, action: PayloadAction<{ entityName: string; }>) {
            const {entityName} = action.payload;
            if (state.featuresWithParentByEntityName[entityName]) {
                const {[entityName]: removed, ...rest} = state.featuresWithParentByEntityName;
                state.featuresWithParentByEntityName = rest;
            }
        },
        setFormEntityChanged(state, action: PayloadAction<{ entityName: string; isChanged: boolean; }>) {
            const {entityName, isChanged} = action.payload;
            return {
                ...state,
                formDataByEntityName: {
                    ...state.formDataByEntityName,
                    ...(
                        state.formDataByEntityName[entityName]
                            ? {
                                [entityName]: {
                                    ...state.formDataByEntityName[entityName],
                                    isChanged
                                }
                            }
                            : {}
                    )
                }
            }
        },
    },
});

export const {
    setEntity,
    setFormEntity,
    setEntityMetadata,
    setFeatureWithParent,
    removeEntity,
    removeFeature,
    setFormEntityChanged,
} = entitySlice.actions;

export default entitySlice.reducer
